// Collection page (sections/main-collection.liquid and its blocks), as the <collection-page> element:
// - Filters, chips, "Clear all" and sort load in place: a change waits ~250ms, the previous request is aborted, the
//   section is fetched with section_id, and its parts marked data-collection-part are swapped (grid, counts, chips,
//   filter states, empty result, end of list), keeping the open sheet, its scroll position and the focus. The URL
//   follows (pushState; back and forward restore). The count is announced in a live region. aria-busy while loading.
// - Price presets are radios; choosing the chosen one again clears it.
// - "Load more" appends the next page's cards (none twice), moves focus to the first new card and announces it. A
//   filter or sort change cancels it, and a page that arrives for an older state is dropped.
// - Back to the list: the address stays the same, but the history entry (history.state) remembers the last page
//   "Load more" added for these filters and sort, and the card in view (saved when a card link is followed, on
//   pagehide and when the page is hidden). When the page is loaded again for that entry (back/forward without the
//   back/forward cache, a reload), the added pages are fetched again (a few at a time, appended in order, none twice)
//   and that card goes back to where it was. The browser's own scroll restoration is off for such an entry (it would
//   scroll before the cards are back). A new visit starts at the first page; a filter or sort change forgets the
//   added pages. A page restored from the back/forward cache is left as it is (nothing was lost).
// - Choosing what is already shown (the current sort, "Clear all" with nothing chosen) sends nothing and adds no
//   history entry.
// - "Hide filters" / "Show filters" (desktop) is remembered for the visit (sessionStorage).
// - Sort dropdown (desktop, a <details> of links): arrow keys, Home/End, Esc, outside click.
// - Phone: the filter form opens as a bottom sheet from "Filter", the sort sheet from "Sort": role=dialog, focus trap,
//   Esc, backdrop, scroll lock, focus back to the button. "Show N pieces" closes and scrolls to the grid.
// Without JavaScript the form submits normally and sort options are links.
// New cards dispatch "lazy-section:loaded" (bubbles) so product-card.js and wishlist.js set them up.
const DE = (window.DE = window.DE || {});
const desktop = window.matchMedia('(min-width: 990px)');
const STORAGE_KEY = 'de-collection-filters';
const DEBOUNCE = 250;
// Back to the list: pages fetched at the same time, and at most this many pages after the first.
const RESTORE_PARALLEL = 4;
const RESTORE_MAX = 50;

const isPlainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

class CollectionPage extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.sectionId = this.dataset.sectionId;
    this.timer = 0;
    this.controller = null;
    this.moreController = null;
    this.sheet = null;
    this.pendingFocus = null;

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onPopstate = this.onPopstate.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onPagehide = this.onPagehide.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('popstate', this.onPopstate);
    window.addEventListener('pagehide', this.onPagehide);
    document.addEventListener('visibilitychange', this.onVisibility);
    desktop.addEventListener('change', this.onBreakpoint);

    this.currentUrl = this.pageUrl(window.location.href);
    try {
      history.replaceState({ ...(history.state || {}), deCollection: this.sectionId }, '');
    } catch {
      // History not writable (sandboxed frame): back and forward reload the page instead.
    }
    this.setSidebar(this.classList.contains('is-sidebar-hidden'), { save: false });
    this.syncPresets();
    this.loadPriceCounts();
    this.restoreMore();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('popstate', this.onPopstate);
    window.removeEventListener('pagehide', this.onPagehide);
    document.removeEventListener('visibilitychange', this.onVisibility);
    desktop.removeEventListener('change', this.onBreakpoint);
    clearTimeout(this.timer);
    this.controller?.abort();
    this.moreController?.abort();
    this.closeSheet({ restoreFocus: false });
    this.bound = false;
  }

  /* ---------- Parts ---------- */
  get form() {
    return this.querySelector('[data-collection-filters]');
  }

  get main() {
    return this.querySelector('.collection__main');
  }

  get grid() {
    return this.querySelector(`#ProductGrid-${CSS.escape(this.sectionId)}`);
  }

  // The page URL (path and query) without section_id or hash.
  pageUrl(href) {
    const url = new URL(href, window.location.href);
    url.searchParams.delete('section_id');
    url.hash = '';
    return url;
  }

  // The page and its parameters, whatever their order or encoding.
  stateKey(url) {
    return `${url.pathname}?${[...url.searchParams].map((pair) => pair.join('=')).sort().join('&')}`;
  }

  sameState(a, b) {
    return !!a && !!b && this.stateKey(a) === this.stateKey(b);
  }

  /* ---------- Filter form ---------- */
  formUrl() {
    const form = this.form;
    const url = new URL(form?.getAttribute('action') || window.location.pathname, window.location.href);
    if (!form) return url;
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (key === 'price-preset' || typeof value !== 'string' || value.trim() === '') continue;
      params.append(key, value.trim());
    }
    const preset = form.querySelector('input[name="price-preset"]:checked');
    const group = preset?.closest('[data-price-group]');
    if (preset && group) {
      if (preset.dataset.gte) params.set(group.dataset.paramMin, preset.dataset.gte);
      if (preset.dataset.lte) params.set(group.dataset.paramMax, preset.dataset.lte);
    }
    url.search = params.toString();
    return url;
  }

  schedule() {
    clearTimeout(this.timer);
    // A response for the previous state must not overwrite this change, nor a page of it be added.
    this.controller?.abort();
    this.moreController?.abort();
    this.scheduled = true;
    this.timer = setTimeout(() => {
      this.scheduled = false;
      this.update(this.formUrl());
    }, DEBOUNCE);
  }

  syncPresets() {
    this.checkedPreset = this.form?.querySelector('input[name="price-preset"]:checked') || null;
  }

  onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.form?.contains(input)) return;
    if (input.name === 'price-preset') this.checkedPreset = input.checked ? input : null;
    // From / to fields apply when focus leaves the pair (onFocusOut) or with Enter, not between the two fields.
    if (input.hasAttribute('data-price-input')) return;
    this.schedule();
  }

  onSubmit(event) {
    if (event.target !== this.form) return;
    event.preventDefault();
    clearTimeout(this.timer);
    this.update(this.formUrl());
  }

  /* ---------- Loading ---------- */
  async update(href, { push = true } = {}) {
    clearTimeout(this.timer);
    // A filter change still waiting (debounce) was just cancelled: the form shows it, so re-render even when the
    // target is the page already shown (e.g. a size tapped, then Clear all within the debounce).
    const pending = this.scheduled;
    this.scheduled = false;
    const target = this.pageUrl(href);
    const shown = this.sameState(target, this.currentUrl);
    // Already shown and nothing on its way: no request, no history entry (the sort menu or sheet has closed).
    if (push && !this.controller && !pending && shown) return;
    if (shown) push = false;
    this.controller?.abort();
    this.moreController?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const request = new URL(target);
    request.searchParams.set('section_id', this.sectionId);
    const main = this.main;
    main?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(request, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      if (controller.signal.aborted) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      this.swap(doc);
      this.currentUrl = target;
      if (push) {
        try {
          history.pushState({ deCollection: this.sectionId }, '', `${target.pathname}${target.search}`);
        } catch {
          // Ignored: the page still shows the new results.
        }
      }
      // New results start at their first page (a pushed entry also inherits the scroll restoration mode).
      this.saveMore(this.firstPage());
      const grid = this.grid;
      this.announce(grid?.dataset.countText || '');
    } catch (error) {
      if (error?.name === 'AbortError' || controller.signal.aborted) return;
      // Fall back to loading the page itself.
      window.location.assign(`${target.pathname}${target.search}`);
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        main?.removeAttribute('aria-busy');
      }
    }
  }

  // Replaces every data-collection-part (by id) with its new version; keeps the sheet's scroll and the focus.
  swap(doc) {
    const active = document.activeElement;
    const activeId = active && this.contains(active) ? active.id : '';
    // A price field being typed in stays as it is (value, caret), in place of its new copy.
    const typing = activeId && active instanceof HTMLInputElement && (active.type === 'number' || active.type === 'text');
    const scroller = this.form?.querySelector('[data-sheet-scroll]');
    const scrollTop = scroller ? scroller.scrollTop : 0;

    // DOMParser parses <noscript> content as elements (scripting is off there): its no-JavaScript price fields would
    // join the form.
    doc.querySelectorAll('noscript').forEach((node) => node.remove());
    this.swapping = true;
    doc.querySelectorAll('[data-collection-part][id]').forEach((fresh) => {
      const current = document.getElementById(fresh.id);
      if (current && this.contains(current)) current.replaceWith(document.importNode(fresh, true));
    });
    this.swapping = false;

    if (scroller && scroller.scrollTop !== scrollTop) scroller.scrollTop = scrollTop;
    this.syncPresets();
    this.loadPriceCounts();
    const grid = this.grid;
    grid?.dispatchEvent(new CustomEvent('lazy-section:loaded', { bubbles: true, detail: { element: grid } }));

    if (active && !active.isConnected) {
      const same = activeId ? document.getElementById(activeId) : null;
      const target = same && this.contains(same) ? same : this.fallbackFocus();
      if (typing && same && target === same) {
        same.replaceWith(active);
        active.focus({ preventScroll: true });
      } else {
        target?.focus({ preventScroll: true });
      }
    }
    this.pendingFocus = null;
  }

  // After a chip is removed: the chip now in its place (or the last one), else the filters button.
  fallbackFocus() {
    const pending = this.pendingFocus;
    if (pending) {
      const container = document.getElementById(pending.container);
      const chips = container ? [...container.querySelectorAll('[data-chip]')] : [];
      if (chips.length) return chips[Math.min(pending.index, chips.length - 1)];
    }
    if (this.sheet) return this.sheet.querySelector('[data-sheet-close]');
    return desktop.matches
      ? this.querySelector('[data-sidebar-toggle]')
      : this.querySelector('[data-filters-open]');
  }

  announce(text) {
    const live = this.querySelector('[data-collection-live]');
    if (!live || !text) return;
    live.textContent = '';
    setTimeout(() => {
      live.textContent = text;
    }, 60);
  }

  /* ---------- Load more ---------- */
  async loadMore(link) {
    if (link.getAttribute('aria-busy') === 'true') return;
    // A filter or sort change is loading: this link belongs to the results it replaces.
    if (this.controller) return;
    this.moreController?.abort();
    const controller = new AbortController();
    this.moreController = controller;
    const grid = this.grid;
    link.setAttribute('aria-busy', 'true');
    const request = new URL(link.href, window.location.href);
    request.searchParams.set('section_id', this.sectionId);
    try {
      const response = await fetch(request, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      // The results changed meanwhile (filter, sort, back/forward): this page belongs to the old ones.
      if (controller.signal.aborted || !link.isConnected || !grid?.isConnected) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const added = this.appendCards(doc, grid);
      this.replaceEnd(doc);
      const page = parseInt(request.searchParams.get('page') || '', 10);
      this.saveMore(page > 0 ? page : this.firstPage() + 1);

      if (added.length) {
        this.cardsLoaded(added);
        added[0].querySelector('.card__link')?.focus();
      }
      this.announce(this.querySelector('[data-end-text]')?.textContent.trim() || '');
    } catch (error) {
      if (error?.name === 'AbortError' || controller.signal.aborted || !link.isConnected) return;
      window.location.assign(link.href);
    } finally {
      if (this.moreController === controller) this.moreController = null;
      link.removeAttribute('aria-busy');
    }
  }

  // Appends a fetched page's cards that the grid doesn't have yet; returns them.
  appendCards(doc, grid, seen = new Set([...grid.children].map((item) => item.dataset.productHandle))) {
    const added = [];
    [...(doc.getElementById(grid.id)?.children || [])].forEach((item) => {
      const handle = item.dataset.productHandle;
      if (seen.has(handle)) return;
      seen.add(handle);
      const node = document.importNode(item, true);
      grid.append(node);
      added.push(node);
    });
    return added;
  }

  // The end of the list (Load more, "You've seen N of M") from a fetched page.
  replaceEnd(doc) {
    const end = this.querySelector(`#CollectionEnd-${CSS.escape(this.sectionId)}`);
    const freshEnd = doc.getElementById(`CollectionEnd-${this.sectionId}`);
    if (end && freshEnd) end.replaceWith(document.importNode(freshEnd, true));
  }

  cardsLoaded(nodes) {
    nodes.forEach((node) => node.dispatchEvent(new CustomEvent('lazy-section:loaded', { bubbles: true, detail: { element: node } })));
  }

  /* ---------- Back to the list ---------- */
  // The page the list starts at: 1, or the page of a "?page=" address opened as such.
  firstPage() {
    const page = parseInt(this.currentUrl?.searchParams.get('page') || '', 10);
    return page > 0 ? page : 1;
  }

  // Merges into this entry's history.state; a null value removes that key.
  writeState(patch) {
    try {
      const state = { ...(history.state || {}), ...patch };
      Object.keys(patch).forEach((key) => {
        if (patch[key] === null) delete state[key];
      });
      history.replaceState(state, '');
    } catch {
      // History not writable (sandboxed frame): coming back starts at the first page.
    }
  }

  // Remembers the last page shown for these filters and sort (none past the first page: the saved spot goes too).
  saveMore(page) {
    const more = !!this.currentUrl && page > this.firstPage();
    if (more) this.writeState({ deCollection: this.sectionId, deMore: { url: this.stateKey(this.currentUrl), page } });
    else if (history.state?.deMore || history.state?.deSpot) this.writeState({ deMore: null, deSpot: null });
    try {
      history.scrollRestoration = more ? 'manual' : 'auto';
    } catch {
      // Ignored: the saved spot is applied after the pages are back anyway.
    }
  }

  // Saves the card to come back to and its distance from the top of the window (and scrollY, in case it is gone):
  // `item` when it is in view (the card whose link was followed), else the first card below the pinned bars.
  saveSpot(item) {
    if (!history.state?.deMore) return;
    const grid = this.grid;
    if (!grid) return;
    const inView = (node) => {
      const rect = node.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    };
    let anchor = item?.isConnected && grid.contains(item) && inView(item) ? item : null;
    if (!anchor) {
      const pinned = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0;
      anchor = [...grid.children].find((node) => node.getBoundingClientRect().bottom > pinned + 1) || null;
    }
    this.writeState({
      deSpot: {
        handle: anchor?.dataset.productHandle || '',
        top: anchor ? Math.round(anchor.getBoundingClientRect().top) : 0,
        y: Math.round(window.scrollY),
      },
    });
  }

  onPagehide() {
    this.saveSpot(this.spotItem);
  }

  onVisibility() {
    if (document.visibilityState === 'hidden') this.saveSpot(this.spotItem);
  }

  // Scrolls the saved card back to its distance from the top of the window; returns the scrollY set.
  goToSpot(spot) {
    const items = [...(this.grid?.children || [])];
    const item = spot.handle ? items.find((node) => node.dataset.productHandle === spot.handle) : null;
    const top = item ? item.getBoundingClientRect().top + window.scrollY - (Number(spot.top) || 0) : Number(spot.y) || 0;
    window.scrollTo(0, Math.max(0, Math.round(top)));
    return window.scrollY;
  }

  // On connect. This entry's history.state has added pages only when the shopper comes back to it (back/forward
  // without the back/forward cache, a reload); a new visit has none and starts at the first page.
  restoreMore() {
    const state = history.state || {};
    const saved = state.deMore;
    if (!saved) return;
    const first = this.firstPage();
    const last = Math.min(parseInt(saved.page, 10) || 0, first + RESTORE_MAX);
    const type = performance.getEntriesByType?.('navigation')?.[0]?.type;
    const link = this.querySelector('a[data-load-more]');
    if (type === 'navigate' || saved.url !== this.stateKey(this.currentUrl) || last <= first || !this.grid || !link) {
      this.saveMore(first);
      return;
    }
    this.restorePages(first + 1, last, state.deSpot, link);
  }

  async restorePages(from, to, spot, link) {
    const grid = this.grid;
    try {
      history.scrollRestoration = 'manual';
    } catch {
      // Ignored.
    }
    this.moreController?.abort();
    const controller = new AbortController();
    this.moreController = controller;
    // Load more waits (aria-busy), a filter or sort change cancels this. The grid is not dimmed: its cards stay valid.
    link.setAttribute('aria-busy', 'true');
    grid.setAttribute('aria-busy', 'true');
    // Scrolling or a key before the pages are back: the shopper's position is kept.
    let moved = false;
    const onInput = () => {
      moved = true;
    };
    const inputs = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    inputs.forEach((type) => window.addEventListener(type, onInput, { capture: true, passive: true }));

    const pages = [];
    for (let page = from; page <= to; page += 1) pages.push(page);
    const texts = pages.map(() => null);
    let next = 0;
    const worker = async () => {
      while (next < pages.length && !controller.signal.aborted) {
        const index = next;
        next += 1;
        const request = new URL(this.currentUrl);
        request.searchParams.set('page', String(pages[index]));
        request.searchParams.set('section_id', this.sectionId);
        try {
          const response = await fetch(request, { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (response.ok) texts[index] = await response.text();
        } catch {
          // Not loaded: the pages before it are kept, Load more offers the rest.
        }
      }
    };

    try {
      await Promise.all(Array.from({ length: Math.min(RESTORE_PARALLEL, pages.length) }, worker));
      if (controller.signal.aborted || !grid?.isConnected) return;
      const seen = new Set([...grid.children].map((item) => item.dataset.productHandle));
      const added = [];
      let shown = from - 1;
      let lastDoc = null;
      for (const [index, text] of texts.entries()) {
        if (text === null) break;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        added.push(...this.appendCards(doc, grid, seen));
        lastDoc = doc;
        shown = pages[index];
      }
      if (lastDoc) this.replaceEnd(lastDoc);
      this.cardsLoaded(added);
      this.saveMore(shown);
      if (moved || !spot) return;
      const y = this.goToSpot(spot);
      // Fonts or images of the page itself may still move the cards: once more on load, unless the shopper scrolled.
      if (document.readyState !== 'complete') {
        window.addEventListener(
          'load',
          () => {
            if (!moved && Math.abs(window.scrollY - y) < 2) this.goToSpot(spot);
          },
          { once: true }
        );
      }
    } finally {
      inputs.forEach((type) => window.removeEventListener(type, onInput, { capture: true }));
      if (this.moreController === controller) this.moreController = null;
      link.removeAttribute('aria-busy');
      grid.removeAttribute('aria-busy');
    }
  }

  /* ---------- Sidebar (desktop) ---------- */
  setSidebar(hidden, { save = true } = {}) {
    this.classList.toggle('is-sidebar-hidden', hidden);
    this.querySelectorAll('[data-sidebar-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(!hidden)));
    if (!save) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, hidden ? 'hidden' : 'open');
    } catch {
      // Storage blocked: the choice lasts until the next page.
    }
  }

  /* ---------- Sort dropdown (desktop) ---------- */
  sortMenu() {
    return this.querySelector('[data-sort-menu]');
  }

  closeSortMenu({ focus = false } = {}) {
    const menu = this.sortMenu();
    if (!menu?.open) return;
    menu.open = false;
    if (focus) menu.querySelector('summary')?.focus();
  }

  // key: ArrowDown / ArrowUp (wrapping), Home, End, or 'current' (the chosen option, else the first).
  moveInMenu(menu, key) {
    const options = [...menu.querySelectorAll('[data-sort-option]')];
    if (!options.length) return;
    const index = options.indexOf(document.activeElement);
    let next;
    if (key === 'Home') next = 0;
    else if (key === 'End') next = options.length - 1;
    else if (key === 'current' || index === -1) next = Math.max(0, options.findIndex((o) => o.hasAttribute('aria-current')));
    else next = (index + (key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    options[next].focus();
  }

  onKeydown(event) {
    // Space on the chosen price preset clears it (browsers ignore Space on a checked radio).
    const preset = event.target.closest?.('input[name="price-preset"]');
    if (preset && event.key === ' ' && preset.checked) {
      event.preventDefault();
      preset.checked = false;
      this.checkedPreset = null;
      this.schedule();
      return;
    }
    // Enter in a price field applies it (the hidden Apply button can't take the form's implicit submission).
    if (event.key === 'Enter' && event.target.matches?.('[data-price-input]')) {
      event.preventDefault();
      clearTimeout(this.timer);
      this.update(this.formUrl());
      return;
    }
    const menu = event.target.closest?.('[data-sort-menu]');
    if (!menu) return;
    const onSummary = event.target.matches('summary');
    if (event.key === 'Escape' && menu.open) {
      event.preventDefault();
      event.stopPropagation();
      this.closeSortMenu({ focus: true });
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    if (onSummary && !menu.open && (event.key === 'Home' || event.key === 'End')) return;
    event.preventDefault();
    if (!menu.open) menu.open = true;
    this.moveInMenu(menu, onSummary && (event.key === 'ArrowDown' || event.key === 'ArrowUp') ? 'current' : event.key);
  }

  onFocusOut(event) {
    const range = event.target.closest?.('[data-price-range]');
    if (range && !this.swapping && !range.contains(event.relatedTarget)) {
      const key = (url) => [...url.searchParams].map((pair) => pair.join('=')).sort().join('&');
      if (!this.currentUrl || key(this.formUrl()) !== key(this.currentUrl)) this.schedule();
    }
    const menu = this.sortMenu();
    if (!menu?.open || !menu.contains(event.target)) return;
    if (!event.relatedTarget || !menu.contains(event.relatedTarget)) menu.open = false;
  }

  onDocumentClick(event) {
    const menu = this.sortMenu();
    if (menu?.open && !menu.contains(event.target)) menu.open = false;
  }

  /* ---------- Sheets (phone) ---------- */
  openSheet(sheet, opener) {
    if (!sheet || desktop.matches) return;
    if (this.sheet) this.closeSheet({ restoreFocus: false });
    this.sheet = sheet;
    this.openerId = opener?.id || '';
    const isForm = sheet === this.form;
    if (isForm) {
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      const title = sheet.querySelector('.cfilters__title');
      if (title?.id) sheet.setAttribute('aria-labelledby', title.id);
      sheet.classList.add('is-open');
    } else {
      sheet.hidden = false;
    }
    const backdrop = this.querySelector('[data-collection-backdrop]');
    if (backdrop) backdrop.hidden = false;
    opener?.setAttribute('aria-expanded', 'true');
    DE.lockScroll?.();
    document.addEventListener('keydown', this.onDocumentKeydown);
    const initial = isForm
      ? sheet.querySelector('[data-sheet-close]')
      : sheet.querySelector('[data-sort-option][aria-current]') || sheet.querySelector('[data-sort-option]');
    this.releaseFocus = DE.trapFocus ? DE.trapFocus(sheet, initial) : null;
    if (!DE.trapFocus) initial?.focus();
  }

  closeSheet({ restoreFocus = true, toGrid = false } = {}) {
    const sheet = this.sheet;
    if (!sheet) return;
    this.sheet = null;
    this.releaseFocus?.();
    this.releaseFocus = null;
    document.removeEventListener('keydown', this.onDocumentKeydown);
    if (sheet === this.form) {
      sheet.classList.remove('is-open');
      sheet.removeAttribute('role');
      sheet.removeAttribute('aria-modal');
      sheet.removeAttribute('aria-labelledby');
    } else {
      sheet.hidden = true;
    }
    const backdrop = this.querySelector('[data-collection-backdrop]');
    if (backdrop) backdrop.hidden = true;
    const opener = this.openerId ? document.getElementById(this.openerId) : null;
    opener?.setAttribute('aria-expanded', 'false');
    DE.unlockScroll?.();
    if (toGrid) this.scrollToResults();
    if (restoreFocus) opener?.focus({ preventScroll: true });
  }

  // Scrolls so the chips and count sit just under the pinned header and Filter / Sort bar.
  scrollToResults() {
    const bar = this.querySelector('.cphone__bar');
    const chips = this.querySelector('.cphone__chips');
    const target = chips && chips.getClientRects().length ? chips : this.querySelector('.cphone__count') || this.grid;
    if (!target) return;
    const pinned = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0) + (bar?.offsetHeight || 0);
    const top = target.getBoundingClientRect().top + window.scrollY - pinned;
    window.scrollTo({ top: Math.max(0, top), behavior: DE.motionOK?.() ? 'smooth' : 'auto' });
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.sheet) return;
    event.preventDefault();
    this.closeSheet();
  }

  onBreakpoint() {
    if (desktop.matches) this.closeSheet({ restoreFocus: false });
    else this.closeSortMenu();
  }

  /* ---------- Clicks ---------- */
  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // A card link followed: that card is the spot to come back to.
    const cardLink = target.closest('a[href]');
    const item = cardLink?.closest('.cgrid__item');
    if (item && this.grid?.contains(item)) {
      this.spotItem = item;
      this.saveSpot(item);
      return;
    }

    const preset = target.closest('input[name="price-preset"]');
    if (preset) {
      // Choosing the chosen preset again clears it (radios can't be unchecked otherwise). After the click has been
      // dispatched: the browser may re-check the radio while it finishes the click.
      if (preset === this.checkedPreset && preset.checked) {
        this.checkedPreset = null;
        setTimeout(() => {
          preset.checked = false;
          this.schedule();
        }, 0);
      } else {
        this.checkedPreset = preset;
      }
      return;
    }

    if (target.closest('[data-sidebar-toggle]')) {
      this.setSidebar(!this.classList.contains('is-sidebar-hidden'));
      return;
    }

    const filtersOpen = target.closest('[data-filters-open]');
    if (filtersOpen) {
      this.openSheet(this.form, filtersOpen);
      return;
    }

    const sortOpen = target.closest('[data-sort-open]');
    if (sortOpen) {
      this.openSheet(this.querySelector('[data-sort-sheet]'), sortOpen);
      return;
    }

    if (target.closest('[data-sheet-close]') || target.closest('[data-collection-backdrop]')) {
      this.closeSheet();
      return;
    }

    if (target.closest('[data-sheet-done]')) {
      this.closeSheet({ toGrid: true });
      return;
    }

    const more = target.closest('a[data-load-more]');
    if (more && isPlainClick(event)) {
      event.preventDefault();
      this.loadMore(more);
      return;
    }

    const link = target.closest('a[data-collection-link]');
    if (!link || !isPlainClick(event)) return;
    event.preventDefault();
    if (link.hasAttribute('data-chip')) {
      const container = link.parentElement;
      this.pendingFocus = { container: container?.id, index: [...container.querySelectorAll('[data-chip]')].indexOf(link) };
    }
    if (link.hasAttribute('data-sort-option')) {
      const inSheet = this.sheet && this.sheet.contains(link);
      if (inSheet) this.closeSheet();
      else this.closeSortMenu({ focus: true });
    }
    this.update(link.href);
  }

  onPopstate(event) {
    if (!event.state || event.state.deCollection !== this.sectionId) return;
    const url = this.pageUrl(window.location.href);
    if (this.currentUrl && url.href === this.currentUrl.href) return;
    this.update(url.href, { push: false });
  }

  /* ---------- Price preset counts (optional block setting) ---------- */
  // Shopify gives no counts for price ranges: one small request per preset, to the render-only section
  // "collection-count", with the other filters kept.
  async loadPriceCounts() {
    const slots = [...this.querySelectorAll('[data-price-count]')];
    if (!slots.length) return;
    const base = this.formUrl();
    const group = this.querySelector('[data-price-group]');
    if (!group) return;
    base.searchParams.delete(group.dataset.paramMin);
    base.searchParams.delete(group.dataset.paramMax);
    const token = (this.countToken = {});
    await Promise.all(
      slots.map(async (slot) => {
        const input = slot.closest('label')?.querySelector('input[name="price-preset"]');
        if (!input) return;
        const url = new URL(base);
        if (input.dataset.gte) url.searchParams.set(group.dataset.paramMin, input.dataset.gte);
        if (input.dataset.lte) url.searchParams.set(group.dataset.paramMax, input.dataset.lte);
        url.searchParams.set('section_id', 'collection-count');
        try {
          const response = await fetch(url, { headers: { Accept: 'text/html' } });
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const count = doc.querySelector('[data-count]')?.dataset.count;
          if (token === this.countToken && slot.isConnected && count !== undefined) slot.textContent = count;
        } catch {
          // No count shown.
        }
      })
    );
  }
}

if (!customElements.get('collection-page')) customElements.define('collection-page', CollectionPage);
